import type React from 'react';

import { useCallback, useEffect, useRef, useState } from 'react';

import { round } from '../utils/math';

type Props = {
  /**
   * A callback function that is called when the value of the range input should be saved.
   *
   * @param value - The current value of the range input.
   * @returns
   */
  onChange: (value: number) => void;
  /**
   * The current value to set on the range input.
   */
  currentValue: number;
  /**
   * The minimum value of the range input
   * @default 0
   */
  min?: number;
  /**
   * The maximum value of the range input
   * @default 100
   */
  max?: number;
  /**
   * The step value for the range input
   * @default 1
   */
  step?: number;
  /**
   * The size of the thumb element in pixels. This is used to calculate the offset of the thumb when calculating the percentage of the input range.
   */
  thumbSize: number;
};

/**
 * Adds support for touch events on input range elements.
 * This hook is tailored for use with a custom range slider component, mainly built for the scrub bar of the video player.
 */
export function useInputRangeTouchSupport({ onChange, thumbSize, currentValue, min = 0, max = 100, step = 1 }: Props) {
  const [inputValue, setInputValue] = useState(currentValue?.toString() || min.toString());
  const pointerDownRef = useRef(false);

  const getInputRangeValue = useCallback(
    (event: React.TouchEvent<HTMLInputElement>): string => {
      const touch = event.changedTouches[0];
      const input = event.target as HTMLInputElement;

      const delta = max - min;

      // Calculate percentage
      let percent;
      const clientRect = input.getBoundingClientRect();
      const thumbSizeCalc = ((100 / clientRect.width) * (thumbSize / 2)) / 100;

      // Determine left percentage
      percent = (100 / clientRect.width) * (touch.clientX - clientRect.left);

      // Don't allow outside bounds
      if (percent < 0) {
        percent = 0;
      } else if (percent > 100) {
        percent = 100;
      }

      // Factor in the thumb offset
      if (percent < 50) {
        percent -= (100 - percent * 2) * thumbSizeCalc;
      } else if (percent > 50) {
        percent += (percent - 50) * 2 * thumbSizeCalc;
      }

      // Find the closest step to the mouse position
      return (min + round(delta * (percent / 100), step)).toString();
    },
    [max, min, step, thumbSize]
  );

  const handleTouchEvent = useCallback(
    (event: React.TouchEvent<HTMLInputElement>) => {
      if (!event.target || !(event.target instanceof HTMLInputElement)) return;

      if (event.type === 'touchstart' || event.type === 'touchmove') {
        pointerDownRef.current = true;
      }

      const inputRangeValue = getInputRangeValue(event);
      setInputValue(inputRangeValue);

      if (event.type === 'touchend') {
        pointerDownRef.current = false;

        onChange(parseFloat(inputRangeValue));
      }
    },
    [getInputRangeValue, onChange]
  );

  const handleMouseEvent = useCallback(
    (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
      if (!event.target || !(event.target instanceof HTMLInputElement)) return;

      switch (event.type) {
        case 'mousedown':
          pointerDownRef.current = true;
          break;
        case 'mouseup':
          onChange(parseFloat(event.target.value));
          pointerDownRef.current = false;
          break;
        case 'mouseleave':
          pointerDownRef.current = false;
          break;
      }
    },
    [onChange]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
      if (!pointerDownRef.current) {
        onChange(parseFloat(event.target.value));
      }
    },
    [onChange]
  );

  useEffect(() => {
    if (!pointerDownRef.current) {
      setInputValue(currentValue.toString());
    }
  }, [currentValue]);

  return {
    onMouseDown: handleMouseEvent,
    onMouseLeave: handleMouseEvent,
    onMouseUp: handleMouseEvent,
    onTouchStart: handleTouchEvent,
    onTouchMove: handleTouchEvent,
    onTouchEnd: handleTouchEvent,
    onChange: handleChange,
    value: inputValue,
    'aria-valuenow': parseFloat(inputValue),
    'aria-valuemin': min,
    'aria-valuemax': max,
    min,
    max,
    step,
  };
}
